/**
 * UserProfileMenu - Presentational user dropdown menu
 * Accepts explicit props; all store/navigation logic lives in the caller (UserMenu).
 */

import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Button,
} from '@/components/ui';
import { useTranslation } from 'react-i18next';
import { NS } from '@/i18n/namespaces';
import { LogOut, UserCircle2, ChevronDown, CreditCard } from 'lucide-react';

export interface UserProfileMenuProps {
  displayName: string;
  showBilling?: boolean;
  className?: string;
  onProfile: () => void;
  onBilling?: () => void;
  onLogout: () => void;
}

export function UserProfileMenu({
  displayName,
  showBilling = false,
  className,
  onProfile,
  onBilling,
  onLogout,
}: UserProfileMenuProps) {
  const { t } = useTranslation(NS.COMMON);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={className ?? 'min-w-8 relative h-8'}>
          <div className="flex items-center space-x-2">
            <span>{displayName}</span>
            <ChevronDown className="h-4 w-4" />
          </div>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-56">
        <DropdownMenuItem onClick={onProfile} className="cursor-pointer">
          <UserCircle2 className="mr-2 h-4 w-4" />
          <span>{t('UserMenu_Profile', { defaultValue: 'Profile' })}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {showBilling && onBilling && (
          <>
            <DropdownMenuItem onClick={onBilling} className="cursor-pointer">
              <CreditCard className="mr-2 h-4 w-4" />
              <span>{t('UserMenu_BillingInformation', { defaultValue: 'Billing Information' })}</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
          </>
        )}

        <DropdownMenuItem onClick={onLogout} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          <span>{t('UserMenu_Logout', { defaultValue: 'Logout' })}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
